import { Box, IconButton, TextField, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { uuidv7, type Poste } from '@iobiz/shared'

/**
 * Le NOMBRE de postes d'une catégorie de services.
 *
 * ⭐ Un poste est une place de travail : deux tables de soin, trois fauteuils.
 * Ce qui compte est COMBIEN il y en a — pas comment on les appelle. Les nommer
 * demandait au commerçant d'inventer un vocabulaire avant de pouvoir ouvrir son
 * agenda, pour une étiquette que personne ne lit ensuite. Ils sont désormais
 * numérotés : 1, 2, 3.
 *
 * ⚠️ Les identifiants existants sont CONSERVÉS quand on augmente ou diminue.
 * Les régénérer aurait détaché les rendez-vous déjà pris de leur place — ils
 * seraient devenus orphelins, invisibles dans le planning.
 */
export function EditeurPostes({
  postes,
  onChange,
}: {
  postes: Poste[]
  onChange: (postes: Poste[]) => void
}): JSX.Element {
  function definir(nombre: number): void {
    const cible = Math.max(0, Math.min(30, nombre))
    const suivants: Poste[] = []
    for (let i = 0; i < cible; i++) {
      const existant = postes[i]
      suivants.push({
        // Le libellé n'est plus qu'un numéro d'ordre : il suit la position.
        id: existant?.id ?? uuidv7(),
        label: String(i + 1),
        position: i,
      })
    }
    onChange(suivants)
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
        Nombre de postes
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 3 }}>
        Une place de travail par poste. L’agenda en fera une ligne, et deux clients ne pourront
        pas occuper le même poste au même moment.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          size="small"
          aria-label="Retirer un poste"
          disabled={postes.length === 0}
          onClick={() => definir(postes.length - 1)}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <TextField
          size="small"
          type="number"
          value={postes.length}
          inputProps={{ min: 0, max: 30, 'aria-label': 'Nombre de postes' }}
          sx={{ width: 96 }}
          onChange={(e) => definir(Number(e.target.value))}
        />
        <IconButton
          size="small"
          aria-label="Ajouter un poste"
          disabled={postes.length >= 30}
          onClick={() => definir(postes.length + 1)}
        >
          <AddIcon fontSize="small" />
        </IconButton>

        <Typography variant="body2" sx={{ color: 'text.secondary', ml: 2 }}>
          {postes.length === 0
            ? 'Aucun poste — cette catégorie n’offrira aucun créneau.'
            : `Postes ${postes.map((_, i) => i + 1).join(', ')}`}
        </Typography>
      </Box>
    </Box>
  )
}
